import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api';
import ComplianceIndicator from '../components/ComplianceIndicator';

interface DashboardData {
    score: number;
    breakdown: { green: number; yellow: number; red: number; total: number };
    amounts: { green: number; yellow: number; red: number };
    byYear: Record<number, { green: number; yellow: number; red: number; total: number }>;
    alerts: Array<{
        id: string;
        date_of_service: string;
        provider: string;
        amount: number;
        irs_compliance_status: string;
    }>;
}

export default function Dashboard() {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const api = useApi();

    useEffect(() => {
        loadDashboard();
    }, []);

    async function loadDashboard() {
        try {
            const result = await api.getComplianceDashboard();
            if (result.success) {
                setData(result.data as DashboardData);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="page-loading">
                <div className="spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    const totalAmount = data ? data.amounts.green + data.amounts.yellow + data.amounts.red : 0;
    const unreimbursedAmount = data ? data.amounts.green + data.amounts.yellow : 0;
    const years = data ? Object.keys(data.byYear).map(Number).sort((a, b) => b - a) : [];
    
    // Calculate estimated tax savings (roughly 25% of eligible expenses)

    // Data for the simple donut chart visualization
    // const greenPct = data && totalAmount > 0 ? (data.amounts.green / totalAmount) * 100 : 0;
    // const yellowPct = data && totalAmount > 0 ? (data.amounts.yellow / totalAmount) * 100 : 0;

    return (
        <div className="dashboard-page">
            <header className="page-header">
                <h1>Dashboard</h1>
                <div className="header-actions">
                    <Link to="/upload" className="btn btn-primary">
                        ➕ Upload Receipt
                    </Link>
                </div>
            </header>

            <div className="dashboard-grid">
                {/* Stat Card 1: Expenses with Documents */}
                <div className="stat-card">
                    <div className="stat-card-content">
                        <div className="stat-icon green">💰</div>
                        <div className="stat-info">
                            <div className="stat-label-with-tooltip">
                                <p className="stat-label">Expenses with Documents</p>
                                <span className="tooltip-trigger">
                                    ?
                                    <span className="tooltip-content">
                                        Recorded totals with supporting documents. Payment, eligibility, HSA date and reimbursement history require separate review in each receipt ledger.
                                    </span>
                                </span>
                            </div>
                            <p className="stat-value">${unreimbursedAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </div>
                    </div>
                </div>

                {/* Stat Card 2: Total Lifetime Eligible */}
                <div className="stat-card">
                    <div className="stat-card-content">
                        <div className="stat-icon blue">✅</div>
                        <div className="stat-info">
                            <div className="stat-label-with-tooltip">
                                <p className="stat-label">Total Recorded</p>
                                <span className="tooltip-trigger">
                                    ?
                                    <span className="tooltip-content">
                                        The gross amount of recorded expenses, including mixed purchases and amounts that may be ineligible or already reimbursed.
                                    </span>
                                </span>
                            </div>
                            <p className="stat-value">${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </div>
                    </div>
                </div>

                {/* Stat Card 3: Receipts Stored */}
                <div className="stat-card">
                    <div className="stat-card-content">
                        <div className="stat-icon amber">📄</div>
                        <div className="stat-info">
                            <div className="stat-label-with-tooltip">
                                <p className="stat-label">Receipts Stored</p>
                                <span className="tooltip-trigger">
                                    ?
                                    <span className="tooltip-content">
                                        Total number of expense records you've uploaded. Original attachments and their hashes are tracked separately from eligibility review.
                                    </span>
                                </span>
                            </div>
                            <p className="stat-value">{data?.breakdown.total || 0}</p>
                        </div>
                    </div>
                </div>

                {/* Stat Card 4: Financial Review */}
                <div className="stat-card">
                    <div className="stat-card-content">
                        <div className="stat-icon purple">💎</div>
                        <div className="stat-info">
                            <div className="stat-label-with-tooltip">
                                <p className="stat-label">Financial Review</p>
                                <span className="tooltip-trigger">
                                    ?
                                    <span className="tooltip-content">
                                        Open each receipt ledger to review payment evidence, item eligibility, the verified HSA date, refunds and reimbursement history.
                                    </span>
                                </span>
                            </div>
                            <p className="stat-value">Required</p>
                        </div>
                    </div>
                </div>

                {/* Recent Receipts Table */}
                <div className="card recent-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2 style={{ margin: 0 }}>Recent Receipts</h2>
                        <Link to="/expenses" style={{ fontSize: '0.875rem', fontWeight: 500 }}>View All</Link>
                    </div>
                    
                    {data?.alerts && data.alerts.length > 0 ? (
                        <table className="recent-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Provider</th>
                                    <th style={{ textAlign: 'right' }}>Amount</th>
                                    <th style={{ textAlign: 'right' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.alerts.slice(0, 5).map((alert) => (
                                    <tr key={alert.id}>
                                        <td style={{ color: 'var(--text-secondary)' }}>{alert.date_of_service}</td>
                                        <td>
                                            <Link to={`/expenses/${alert.id}`} style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                                                {alert.provider || 'Unknown'}
                                            </Link>
                                        </td>
                                        <td className="amount" style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                                            ${alert.amount.toFixed(2)}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <ComplianceIndicator status={alert.irs_compliance_status as 'red' | 'yellow' | 'green'} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            <p>No receipts logged yet.</p>
                            <Link to="/upload" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                                Upload your first receipt
                            </Link>
                        </div>
                    )}
                </div>

                {/* Compliance Score Card */}
                <div className="card chart-card">
                    <h2>Documentation Coverage</h2>
                    <div className="chart-container">
                        <div
                            className="score-circle"
                            style={{
                                '--score': data?.score || 0,
                                '--color': (data?.score || 0) >= 80 ? 'var(--green)' : (data?.score || 0) >= 50 ? 'var(--yellow)' : 'var(--red)',
                            } as React.CSSProperties}
                        >
                            <span className="score-value">{data?.score || 0}%</span>
                        </div>
                    </div>
                    <p className="score-label" style={{ textAlign: 'center' }}>
                        {(data?.score || 0) >= 80 ? '🎉 Excellent - Documents Present' : (data?.score || 0) >= 50 ? '👍 Good - Some Docs Needed' : '⚠️ Needs Attention'}
                    </p>
                    
                    {/* Documentation Status Mini Stats */}
                    <div className="stats-grid" style={{ marginTop: '1.5rem' }}>
                        <div className="stat-item">
                            <span className="stat-icon">🟢</span>
                            <span className="stat-value">{data?.breakdown.green || 0}</span>
                            <span className="stat-label">Documents Present</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-icon">🟡</span>
                            <span className="stat-value">{data?.breakdown.yellow || 0}</span>
                            <span className="stat-label">Partial</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-icon">🔴</span>
                            <span className="stat-value">{data?.breakdown.red || 0}</span>
                            <span className="stat-label">Needs Docs</span>
                        </div>
                    </div>
                </div>

                {/* By Year Breakdown */}
                <div className="card year-card">
                    <h2>By Tax Year</h2>
                    {years.length > 0 ? (
                        <div className="year-list">
                            {years.map((year) => (
                                <div key={year} className="year-row">
                                    <span className="year-label">{year}</span>
                                    <div className="year-bars">
                                        <div
                                            className="bar bar-green"
                                            style={{ width: `${(data!.byYear[year].green / data!.byYear[year].total) * 100}%` }}
                                            title={`Green: ${data!.byYear[year].green}`}
                                        />
                                        <div
                                            className="bar bar-yellow"
                                            style={{ width: `${(data!.byYear[year].yellow / data!.byYear[year].total) * 100}%` }}
                                            title={`Yellow: ${data!.byYear[year].yellow}`}
                                        />
                                        <div
                                            className="bar bar-red"
                                            style={{ width: `${(data!.byYear[year].red / data!.byYear[year].total) * 100}%` }}
                                            title={`Red: ${data!.byYear[year].red}`}
                                        />
                                    </div>
                                    <span className="year-total">{data!.byYear[year].total}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>
                            No data yet
                        </p>
                    )}
                    
                    <div className="chart-legend" style={{ marginTop: '1rem' }}>
                        <div className="legend-item">
                            <div className="legend-dot green"></div>
                            <span>Documents Present</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-dot" style={{ background: 'var(--yellow)' }}></div>
                            <span>Partial</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-dot" style={{ background: 'var(--red)' }}></div>
                            <span>Needs Docs</span>
                        </div>
                    </div>
                </div>

                {/* Missing Documentation Alerts */}
                <div className="card alerts-card">
                    <h2>⚠️ Missing Documentation</h2>
                    {data?.alerts && data.alerts.filter(a => a.irs_compliance_status !== 'green').length > 0 ? (
                        <ul className="alerts-list">
                            {data.alerts
                                .filter(a => a.irs_compliance_status !== 'green')
                                .slice(0, 5)
                                .map((alert) => (
                                    <li key={alert.id}>
                                        <Link to={`/expenses/${alert.id}`} className="alert-item">
                                            <ComplianceIndicator status={alert.irs_compliance_status as 'red' | 'yellow' | 'green'} />
                                            <span className="alert-provider">{alert.provider || 'Unknown'}</span>
                                            <span className="alert-amount">${alert.amount.toFixed(2)}</span>
                                            <span className="alert-date">{alert.date_of_service}</span>
                                        </Link>
                                    </li>
                                ))}
                        </ul>
                    ) : (
                        <div className="no-alerts">🎉 All expenses have documentation!</div>
                    )}
                </div>
            </div>
        </div>
    );
}
